import { useState, useEffect } from 'react'
import { X, Brain, ChevronDown, ChevronUp, Save, Check, FileText, FolderTree } from 'lucide-react'
import { saveMemoryDocument, type MemoryDocument } from '@/shared/lib/api'
import { useToast } from '@/shared/hooks/useToast'
import { useDiscoveryResultStore } from '@/shared/stores/discoveryResultStore'

interface DocumentWithState extends MemoryDocument {
  saved: boolean
  saving: boolean
}

export function DiscoveryResultModal() {
  const { result, repositoryPath, isOpen, closeResult, clearResult } = useDiscoveryResultStore()
  const [documents, setDocuments] = useState<DocumentWithState[]>([])
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null)
  const { showToast } = useToast()

  // Update documents when result changes
  useEffect(() => {
    if (result?.documents && result.documents.length > 0) {
      setDocuments(result.documents.map(d => ({ ...d, saved: false, saving: false })))
    } else {
      setDocuments([])
    }
  }, [result])
  
  const onClose = () => {
    closeResult()
  }
  
  const handleFullClose = () => {
    clearResult()
    setDocuments([])
  }

  const handleSaveDocument = async (doc: DocumentWithState) => {
    setDocuments(prev => prev.map(d => 
      d.path === doc.path ? { ...d, saving: true } : d
    ))

    try {
      await saveMemoryDocument({
        repository_path: repositoryPath,
        file_path: doc.path,
        content: doc.content
      })
      
      setDocuments(prev => prev.map(d => 
        d.path === doc.path ? { ...d, saved: true, saving: false } : d
      ))
      showToast(`Saved .memory/${doc.path}`, 'success')
    } catch {
      setDocuments(prev => prev.map(d =>
        d.path === doc.path ? { ...d, saving: false } : d
      ))
      showToast('Failed to save document', 'error')
    }
  }

  const handleSaveAll = async () => {
    const unsaved = documents.filter(d => !d.saved)
    for (const doc of unsaved) {
      await handleSaveDocument(doc)
    }
  }

  const toggleExpand = (path: string) => {
    setExpandedDoc(expandedDoc === path ? null : path)
  }

  if (!isOpen || !result) return null

  const unsavedCount = documents.filter(d => !d.saved).length

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl bg-[#1a1a1a] border border-white/10 rounded-lg shadow-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded bg-purple-500/20">
              <Brain size={18} className="text-purple-400" />
            </div>
            <div>
              <h3 className="font-medium text-white">Discovery Results</h3>
              <p className="text-xs text-white/50">
                {result.architecture_type} • {result.primary_languages.join(', ')}
              </p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/10 rounded transition-colors"
          >
            <X size={18} className="text-white/50" />
          </button>
        </div>

        {/* Zones Summary */}
        <div className="p-4 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <FolderTree size={14} className="text-white/40" />
            <span className="text-sm font-medium text-white/80">
              {result.zones.length} Zone{result.zones.length !== 1 ? 's' : ''} Identified
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {result.zones.map(zone => (
              <div 
                key={zone.path}
                className="px-2 py-1 bg-white/5 border border-white/10 rounded text-xs"
              >
                <span className="text-white/80">{zone.name}</span>
                <span className="text-white/40 ml-1">({zone.pattern_type})</span>
              </div>
            ))}
          </div>
        </div>

        {/* Documents List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-white/40" />
              <span className="text-sm font-medium text-white/80">
                Generated Documents ({documents.length})
              </span>
            </div>
            {unsavedCount > 0 && (
              <button
                onClick={handleSaveAll}
                className="text-xs px-2 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded transition-colors"
              >
                Save All ({unsavedCount})
              </button>
            )}
          </div>

          {documents.map(doc => (
            <div 
              key={doc.path}
              className="rounded-lg bg-white/5 border border-white/5 overflow-hidden"
            >
              {/* Document Header */}
              <div 
                className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/5"
                onClick={() => toggleExpand(doc.path)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`p-1.5 rounded ${
                    doc.doc_type === 'overview' ? 'bg-blue-500/20 text-blue-400' :
                    doc.doc_type === 'zone' ? 'bg-green-500/20 text-green-400' :
                    'bg-white/10 text-white/60'
                  }`}>
                    <FileText size={12} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-mono text-white/90 truncate">
                      .memory/{doc.path}
                    </p>
                    {doc.zone_name && (
                      <p className="text-xs text-white/40">{doc.zone_name}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {doc.saved ? (
                    <span className="flex items-center gap-1 text-xs text-emerald-400">
                      <Check size={12} />
                      Saved
                    </span>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleSaveDocument(doc)
                      }}
                      disabled={doc.saving}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded transition-colors disabled:opacity-50"
                    >
                      <Save size={12} />
                      {doc.saving ? 'Saving...' : 'Save'}
                    </button>
                  )}
                  {expandedDoc === doc.path ? (
                    <ChevronUp size={16} className="text-white/40" />
                  ) : (
                    <ChevronDown size={16} className="text-white/40" />
                  )}
                </div>
              </div>

              {/* Document Content Preview */}
              {expandedDoc === doc.path && (
                <div className="border-t border-white/5 p-3 bg-black/20">
                  <pre className="text-xs text-white/70 whitespace-pre-wrap font-mono max-h-64 overflow-y-auto">
                    {doc.content}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-white/10 shrink-0">
          <p className="text-xs text-white/40">
            Documents will be saved to <code className="text-white/60">.memory/</code> in your repo
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm rounded transition-colors"
            >
              Minimize
            </button>
            <button
              onClick={handleFullClose}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm rounded transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
